import React, { useCallback, useRef, useState } from "react";
import { api } from "@/lib/api";
import useFetch from "@/lib/useFetch";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Plus, Upload, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ToolFormDialog from "@/pages/manager/ToolFormDialog";

const emptyForm = {
  code: "",
  name: "",
  model: "",
  vibration_m_s2: "",
  category: "",
  notes: "",
};

function ToolsTable({ tools, onEdit, onDelete }) {
  if (tools.length === 0) {
    return <p className="p-10 text-center text-slate-500">No tools yet.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="text-left text-xs uppercase tracking-wider text-slate-500 p-4">Code</th>
            <th className="text-left text-xs uppercase tracking-wider text-slate-500 p-4">Name</th>
            <th className="text-left text-xs uppercase tracking-wider text-slate-500 p-4">Model</th>
            <th className="text-left text-xs uppercase tracking-wider text-slate-500 p-4">Category</th>
            <th className="text-left text-xs uppercase tracking-wider text-slate-500 p-4">Vibration</th>
            <th className="text-right text-xs uppercase tracking-wider text-slate-500 p-4 w-32">Actions</th>
          </tr>
        </thead>
        <tbody>
          {tools.map((t) => (
            <tr key={t.id} className="border-b last:border-0">
              <td className="p-4 text-sm font-mono text-slate-900">{t.code}</td>
              <td className="p-4 text-sm text-slate-900 font-medium">{t.name}</td>
              <td className="p-4 text-sm text-slate-700">{t.model || "—"}</td>
              <td className="p-4 text-sm text-slate-700">{t.category || "—"}</td>
              <td className="p-4 text-sm font-mono text-slate-900">
                {t.vibration_m_s2.toFixed(1)} m/s²
              </td>
              <td className="p-4 text-right">
                <button
                  onClick={() => onEdit(t)}
                  className="text-slate-600 hover:text-slate-900 p-2"
                  data-testid={`tool-edit-${t.id}`}
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onDelete(t)}
                  className="text-red-600 hover:text-red-800 p-2"
                  data-testid={`tool-delete-${t.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Tools() {
  const fetcher = useCallback(() => api.get("/tools").then((r) => r.data), []);
  const { data, loading, reload } = useFetch(fetcher);
  const tools = data || [];
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const fileRef = useRef(null);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      code: t.code,
      name: t.name,
      model: t.model || "",
      vibration_m_s2: String(t.vibration_m_s2),
      category: t.category || "",
      notes: t.notes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    const payload = { ...form, vibration_m_s2: parseFloat(form.vibration_m_s2) };
    if (!payload.code || !payload.name || Number.isNaN(payload.vibration_m_s2)) {
      toast.error("Code, name and vibration are required");
      return;
    }
    try {
      if (editing) {
        await api.put(`/tools/${editing.id}`, payload);
        toast.success("Tool updated");
      } else {
        await api.post("/tools", payload);
        toast.success("Tool added");
      }
      setOpen(false);
      reload();
    } catch (err) {
      console.error("Tool save failed:", err);
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete tool "${t.name}"?`)) return;
    try {
      await api.delete(`/tools/${t.id}`);
      toast.success("Tool deleted");
      reload();
    } catch (err) {
      console.error("Tool delete failed:", err);
      toast.error("Delete failed");
    }
  };

  const onImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data: result } = await api.post("/tools/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${result.created} tools`);
      reload();
    } catch (err) {
      console.error("Tool import failed:", err);
      toast.error("Import failed");
    }
    e.target.value = "";
  };

  let body;
  if (loading) body = <p className="p-6 text-slate-500">Loading…</p>;
  else body = <ToolsTable tools={tools} onEdit={openEdit} onDelete={del} />;

  return (
    <div className="space-y-6" data-testid="tools-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Manage</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mt-1">
            Tools
          </h1>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={onImport}
            data-testid="tools-import-input"
          />
          <Button
            variant="outline"
            onClick={() => fileRef.current?.click()}
            className="gap-2"
            data-testid="tools-import-btn"
          >
            <Upload className="h-4 w-4" /> Import CSV
          </Button>
          <Button
            onClick={openCreate}
            className="gap-2 bg-slate-900 hover:bg-slate-800"
            data-testid="tools-add-btn"
          >
            <Plus className="h-4 w-4" /> Add tool
          </Button>
        </div>
      </div>

      <Card className="border-slate-200">
        <p className="px-6 py-3 text-xs text-slate-500 border-b">
          CSV columns:{" "}
          <span className="font-mono">
            code, name, model, vibration_m_s2, category, notes
          </span>
        </p>
        {body}
      </Card>

      <ToolFormDialog
        open={open}
        onOpenChange={setOpen}
        form={form}
        setForm={setForm}
        onSave={save}
        isEdit={!!editing}
      />
    </div>
  );
}
